import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { GetSearchTerm, GetSearchTermVariables } from './__generated__/GetSearchTerm'
import { LoadMask, Spacer } from '@vestaboard/installables'

import { Alert } from '@material-ui/lab'
import { Link } from '../link'
import React from 'react'
import { Typography } from '@material-ui/core'
import { gql } from 'apollo-boost'
import moment from 'moment'
import { useParams } from 'react-router'
import { useQuery } from '@apollo/react-hooks'

enum SearchTermAnalyticsBreakdownPeriod {
  day = 'day',
  hour = 'hour',
  month = 'month',
  week = 'week'
}

const QUERY = gql`
  query GetSearchTerm($term: String!, $period: SearchTermAnalyticsBreakdownPeriod!, $start: String!, $end: String!) {
    getSearchTermV2(term: $term) {
      term
      count
      breakdown(input: { period: $period, start: $start, end: $end }) {
        key
        value
      }
    }
  }
`

const useGetSearchTerm = (variables: GetSearchTermVariables) => {
  console.log({ variables })
  return useQuery<GetSearchTerm, GetSearchTermVariables>(QUERY, {
    variables
  })
}

const HourlyChart = (props: { term: string }) => {
  const today = moment().format('YYYY, M, D')
  const tomorrow = moment().add(1, 'day').format('YYYY, M, D')

  const { data, loading, error } = useGetSearchTerm({
    term: props.term,
    period: SearchTermAnalyticsBreakdownPeriod.hour,
    start: `date(${today})`,
    end: `date(${tomorrow})`
  })

  const hourValues = (data?.getSearchTermV2?.breakdown || []).reduce(
    (prev, item) => ({
      ...prev,
      [moment(new Date((item.key as any) as string)).format('h:mm A')]: item.value
    }),
    {}
  ) as { [key: string]: number }

  const everyHourForPastDay = Array.from({ length: 24 }, (_, i) => moment().subtract(i, 'hour'))
    .sort((a, b) => (a.isBefore(b) ? -1 : a.isAfter(b) ? 1 : 0))
    .map(date => date.format('h:00 A'))

  const dayValues = everyHourForPastDay.map(key => ({
    key,
    value: hourValues[key] || 0
  }))

  return loading ? (
    <LoadMask />
  ) : error ? (
    <Alert severity='error'>{error.message}</Alert>
  ) : (
    <>
      <Typography variant='h4'>{data?.getSearchTermV2?.count} total searches</Typography>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Searches in Past Day</Typography>
      <Spacer size='large' />
      <ResponsiveContainer width='100%' height={300}>
        <BarChart width={150} height={40} data={dayValues}>
          <CartesianGrid strokeDasharray='3 3' />
          <Bar dataKey='value' fill='#000' />
          <XAxis dataKey='key' />
          <YAxis dataKey='value' allowDecimals={false} />
          <Tooltip />
        </BarChart>
      </ResponsiveContainer>
    </>
  )
}

const DailyChart = (props: { term: string }) => {
  const today = moment().format('YYYY, M, D')
  const thirtyDaysAgo = moment().add(30, 'day').format('YYYY, M, D')

  const { data, loading, error } = useGetSearchTerm({
    term: props.term,
    period: SearchTermAnalyticsBreakdownPeriod.day,
    start: `date(${today})`,
    end: `date(${thirtyDaysAgo})`
  })

  const dateValues = (data?.getSearchTermV2?.breakdown || []).reduce(
    (prev, item) => ({
      ...prev,
      [moment(new Date((item.key as any) as string)).format('M/D')]: item.value
    }),
    {}
  ) as { [key: string]: number }

  const everyDateForPastMonth = Array.from({ length: 30 }, (_, i) => moment().subtract(i, 'day'))
    .sort((a, b) => (a.isBefore(b) ? -1 : a.isAfter(b) ? 1 : 0))
    .map(date => date.format('M/D'))

  const monthValues = everyDateForPastMonth.map(key => ({
    key,
    value: dateValues[key] || 0
  }))

  return loading ? (
    <LoadMask />
  ) : error ? (
    <Alert severity='error'>{error.message}</Alert>
  ) : (
    <>
      <Spacer size='extraLarge' />
      <Typography variant='h5'>Searches in Past Month</Typography>
      <Spacer size='large' />
      <ResponsiveContainer width='100%' height={300}>
        <BarChart width={150} height={40} data={monthValues}>
          <CartesianGrid strokeDasharray='3 3' />
          <Bar dataKey='value' fill='#000' />
          <XAxis dataKey='key' />
          <YAxis dataKey='value' allowDecimals={false} />
          <Tooltip />
        </BarChart>
      </ResponsiveContainer>
    </>
  )
}

export const Term = () => {
  const { term } = useParams<{ term: string }>()
  return (
    <>
      <Link to='/search-terms/trending'>
        <Typography variant='h6'>Trending</Typography>
      </Link>{' '}
      <Typography variant='h3'>{term}</Typography>
      <HourlyChart term={term} />
      <DailyChart term={term} />
    </>
  )
}
